import { Fragment, memo, useMemo, useState } from "react";
import {
  clampWidth,
  columnWidth,
  cycleSort,
  groupByAccount,
  moveColumnBy,
  moveColumnTo,
  resizeColumn,
  sortRows,
  subtotals,
  visibleColumns,
  zoneAbbreviation,
  type ColumnDef,
  type ColumnState,
  type TableDef,
} from "@helm/core";
import { formatCents, pnlTone } from "@helm/ui";
import { formatCell, isNumericColumn, pendingTitle, type CellCtx } from "./cells";
import "./tables.css";

/**
 * A HELM grid driven by a column registry (`@helm/core` tables): the columns the panel's state
 * shows, in its order and widths, sortable by header click, reorderable by dragging a header
 * (Alt+←/→ on a focused header), resizable by dragging a header's right edge (←/→ on the focused
 * handle). "All accounts" tables group rows by account with per-account subtotal rows and a total
 * (Q-W1); only money columns flagged `subtotal` are summed. A trailing filler column takes the
 * spare width so the other columns keep exactly the widths set.
 *
 * The header row and each data row are memoized: a quote that moves one position's P&L re-renders
 * that row, not the header or the other rows. That needs stable `ctx`, `renderCell` and `rowProps`
 * from the table (memoize them) and row objects that keep their identity while unchanged.
 */

export type Grouping = {
  accountOf: (accountId: string) => string;
  /** Account display order (the account list). */
  order: readonly string[];
  /** "subtotal": rows then a subtotal row per account + a total (Positions); "header": a count row before each group (Orders). */
  style: "subtotal" | "header";
  /** For header rows: "order" → "3 orders". */
  noun?: string;
};

export type DataTableProps<R extends { accountId: string }> = {
  def: TableDef<R>;
  state: ColumnState;
  onStateChange: (s: ColumnState) => void;
  rows: readonly R[];
  rowKey: (r: R) => string;
  multiAccount: boolean;
  /** Group by account (only applied while several accounts show). */
  grouping?: Grouping;
  /** A footer total row over every row (Accounts). */
  totalLabel?: string;
  empty: { title: string; text: string };
  ctx: CellCtx<R>;
  zone: string;
  /** Custom cell content (buttons, tags); undefined = the registry formatting. */
  renderCell?: (col: ColumnDef<R>, row: R) => React.ReactNode;
  rowProps?: (r: R) => React.HTMLAttributes<HTMLTableRowElement>;
};

const RESIZE_STEP = 8;

export function DataTable<R extends { accountId: string }>(props: DataTableProps<R>): React.JSX.Element {
  const { def, state, onStateChange, rows, multiAccount, ctx } = props;
  const cols = useMemo(() => visibleColumns(state, def, { multiAccount }), [state, def, multiAccount]);
  const [live, setLive] = useState<{ id: string; width: number } | null>(null);
  const zoneLabel = zoneAbbreviation(props.zone);

  const widthOf = (c: ColumnDef<R>): number => (live?.id === c.id ? live.width : columnWidth(state, c));
  const total = cols.reduce((n, c) => n + widthOf(c), 0);
  const span = cols.length + 1;

  const row = (r: R): React.JSX.Element => (
    <DataRow
      key={props.rowKey(r)}
      row={r}
      cols={cols}
      ctx={ctx}
      renderCell={props.renderCell}
      rowProps={props.rowProps}
    />
  );

  const summary = (key: string, label: string, list: readonly R[], className: string): React.JSX.Element => {
    const sums = subtotals(list, cols);
    const lead = cols.findIndex((c) => sums.has(c.id));
    const run = lead < 0 ? cols.length : lead;
    return (
      <tr key={key} className={className}>
        {run > 0 && <td colSpan={run}>{label}</td>}
        {cols.slice(run).map((c, i) => {
          const v = sums.get(c.id);
          if (v === undefined) return <td key={c.id} />;
          const signed = c.kind === "pnl";
          return (
            <td
              key={c.id}
              className={`num${signed ? ` tone-${pnlTone(v === null ? null : Number(v))}` : ""}`}
              title={v === null ? "Unknown while any row's value is unknown" : undefined}
            >
              {run === 0 && i === 0 && <span className="sr-only">{label}: </span>}
              {formatCents(v, { privacy: ctx.privacy, signed })}
            </td>
          );
        })}
        <td className="col-fill" aria-hidden="true" />
      </tr>
    );
  };

  const g = props.grouping;
  let body: React.ReactNode;
  if (rows.length === 0) {
    body = (
      <tr className="grid-empty">
        <td colSpan={span}>
          <div className="empty-inline">
            <span className="empty-title">{props.empty.title}</span>
            <span className="muted">{props.empty.text}</span>
          </div>
        </td>
      </tr>
    );
  } else if (multiAccount && g) {
    const groups = groupByAccount(rows, g.order);
    body = groups.map((grp) => (
      <Fragment key={grp.accountId}>
        {g.style === "header" && (
          <tr className="grid-subtotal">
            <td colSpan={span}>
              {g.accountOf(grp.accountId)} · {grp.rows.length} {g.noun ?? "row"}
              {grp.rows.length === 1 ? "" : "s"}
            </td>
          </tr>
        )}
        {sortRows(grp.rows, def, state.sort).map(row)}
        {g.style === "subtotal" &&
          summary(
            `s-${grp.accountId}`,
            `Subtotal · ${g.accountOf(grp.accountId)}`,
            grp.rows,
            "grid-subtotal",
          )}
      </Fragment>
    ));
    if (g.style === "subtotal")
      body = (
        <>
          {body}
          {summary("total", `Total · ${String(groups.length)} accounts`, rows, "grid-total")}
        </>
      );
  } else {
    body = sortRows(rows, def, state.sort).map(row);
  }

  return (
    <table className="grid grid-cols" style={{ width: total, minWidth: "100%" }}>
      <colgroup>
        {cols.map((c) => (
          <col key={c.id} style={{ width: widthOf(c) }} />
        ))}
        <col />
      </colgroup>
      <thead>
        <HeaderRow
          def={def}
          cols={cols}
          state={state}
          onStateChange={onStateChange}
          zoneLabel={zoneLabel}
          live={live}
          setLive={setLive}
        />
      </thead>
      <tbody>{body}</tbody>
      {props.totalLabel !== undefined && rows.length > 1 && (
        <tfoot>{summary("tfoot", props.totalLabel, rows, "grid-total")}</tfoot>
      )}
    </table>
  );
}

type Live = { id: string; width: number } | null;

/** The header row: sort, drag to reorder, resize. Re-renders with the columns, not with the rows. */
function HeaderRowOf<R>({
  def,
  cols,
  state,
  onStateChange,
  zoneLabel,
  live,
  setLive,
}: {
  def: TableDef<R>;
  cols: readonly ColumnDef<R>[];
  state: ColumnState;
  onStateChange: (s: ColumnState) => void;
  zoneLabel: string;
  /** The column being resized and its width while the pointer is down. */
  live: Live;
  setLive: (l: Live) => void;
}): React.JSX.Element {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const widthOf = (c: ColumnDef<R>): number => (live?.id === c.id ? live.width : columnWidth(state, c));

  const header = (c: ColumnDef<R>): React.JSX.Element => {
    const sorted = state.sort?.id === c.id ? state.sort.dir : null;
    const sortable = c.unsortable !== true;
    const label = c.kind === "time" ? `${c.label} ${zoneLabel}` : c.label;
    const inState = state.columns.includes(c.id);
    const tip = [c.definition, pendingTitle(c)].filter(Boolean).join("\n\n");
    const drop =
      dropId === c.id && dragId !== null && dragId !== c.id
        ? state.columns.indexOf(dragId) < state.columns.indexOf(c.id)
          ? " drop-after"
          : " drop-before"
        : "";
    return (
      <th
        key={c.id}
        scope="col"
        className={`${isNumericColumn(c) ? "num " : ""}th-col${drop}${dragId === c.id ? " dragging" : ""}`}
        aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
        draggable={inState}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", c.id);
          setDragId(c.id);
        }}
        onDragOver={(e) => {
          if (dragId === null || !inState) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          if (dropId !== c.id) setDropId(c.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (dragId !== null) onStateChange(moveColumnTo(state, dragId, c.id));
          setDragId(null);
          setDropId(null);
        }}
        onDragEnd={() => {
          setDragId(null);
          setDropId(null);
        }}
      >
        <button
          type="button"
          className="th-btn"
          title={`${tip}\n\n${sortable ? "Click to sort · " : ""}drag or Alt+←/→ to move`}
          onClick={() => {
            if (sortable) onStateChange(cycleSort(state, def, c.id));
          }}
          onKeyDown={(e) => {
            if (!e.altKey || !inState || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
            e.preventDefault();
            onStateChange(moveColumnBy(state, c.id, e.key === "ArrowLeft" ? -1 : 1));
          }}
        >
          <span className="th-label">{label}</span>
          {sorted !== null && (
            <span className="th-sort" aria-hidden="true">
              {sorted === "asc" ? "▲" : "▼"}
            </span>
          )}
        </button>
        <span
          className={live?.id === c.id ? "col-resize active" : "col-resize"}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${c.label}`}
          aria-valuemin={c.minWidth}
          aria-valuemax={800}
          aria-valuenow={widthOf(c)}
          tabIndex={0}
          title="Drag (or ←/→) to resize"
          draggable={false}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const start = widthOf(c);
            const el = e.currentTarget;
            el.setPointerCapture(e.pointerId);
            let latest = start;
            const move = (ev: PointerEvent): void => {
              latest = clampWidth(c, start + ev.clientX - startX);
              setLive({ id: c.id, width: latest });
            };
            const up = (): void => {
              el.removeEventListener("pointermove", move);
              el.removeEventListener("pointerup", up);
              el.removeEventListener("pointercancel", up);
              setLive(null);
              if (latest !== start) onStateChange(resizeColumn(state, def, c.id, latest));
            };
            el.addEventListener("pointermove", move);
            el.addEventListener("pointerup", up);
            el.addEventListener("pointercancel", up);
          }}
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            e.preventDefault();
            const d = e.key === "ArrowLeft" ? -RESIZE_STEP : RESIZE_STEP;
            onStateChange(resizeColumn(state, def, c.id, widthOf(c) + d));
          }}
        />
      </th>
    );
  };

  return (
    <tr>
      {cols.map(header)}
      <th className="col-fill" aria-hidden="true" />
    </tr>
  );
}

const HeaderRow = memo(HeaderRowOf) as typeof HeaderRowOf;

/** One data row: re-renders when its row object, the columns or the table's formatters change. */
function DataRowOf<R>({
  row: r,
  cols,
  ctx,
  renderCell,
  rowProps,
}: {
  row: R;
  cols: readonly ColumnDef<R>[];
  ctx: CellCtx<R>;
  renderCell: ((col: ColumnDef<R>, row: R) => React.ReactNode) | undefined;
  rowProps: ((r: R) => React.HTMLAttributes<HTMLTableRowElement>) | undefined;
}): React.JSX.Element {
  const extra = rowProps?.(r) ?? {};
  return (
    <tr {...extra}>
      {cols.map((c) => {
        const custom = renderCell?.(c, r);
        if (custom !== undefined) {
          return (
            <td key={c.id} className={isNumericColumn(c) ? "num" : undefined}>
              {custom}
            </td>
          );
        }
        const out = formatCell(c, r, ctx);
        return (
          <td key={c.id} className={out.className || undefined} title={out.title}>
            {out.text}
          </td>
        );
      })}
      <td className="col-fill" aria-hidden="true" />
    </tr>
  );
}

const DataRow = memo(DataRowOf) as typeof DataRowOf;
